import { createContext, useContext, useState, useCallback } from "react";
import { track } from "@/lib/analytics";

export type AuthView = "signup" | "signin" | "forgot-password";

/** Why the modal opened, so it can speak to that moment and an email to pre-fill. */
export interface AuthPrefill {
  email?: string;
  reason?: "plan" | "community";
}

interface AuthModalState {
  isOpen: boolean;
  view: AuthView;
  prefill: AuthPrefill;
  /** Safe to pass straight to onClick: a click event is ignored as options. */
  openSignUp: (opts?: AuthPrefill | unknown) => void;
  openSignIn: () => void;
  openForgotPassword: () => void;
  setView: (view: AuthView) => void;
  close: () => void;
}

const AuthModalContext = createContext<AuthModalState | null>(null);

export function AuthModalProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState<AuthView>("signup");
  const [prefill, setPrefill] = useState<AuthPrefill>({});

  const openSignUp = useCallback((opts?: AuthPrefill | unknown) => {
    const o = opts && typeof opts === "object" && !("nativeEvent" in opts) ? (opts as AuthPrefill) : {};
    setPrefill({ email: o.email, reason: o.reason });
    track("signup_opened", { reason: o.reason ?? "general" });
    setView("signup");
    setIsOpen(true);
  }, []);

  const openSignIn = useCallback(() => {
    setPrefill({});
    setView("signin");
    setIsOpen(true);
  }, []);

  const openForgotPassword = useCallback(() => {
    setView("forgot-password");
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  return (
    <AuthModalContext.Provider
      value={{ isOpen, view, prefill, openSignUp, openSignIn, openForgotPassword, setView, close }}
    >
      {children}
    </AuthModalContext.Provider>
  );
}

export function useAuthModal() {
  const ctx = useContext(AuthModalContext);
  if (!ctx) throw new Error("useAuthModal must be used within AuthModalProvider");
  return ctx;
}
